import { ExclamationCircleOutlined } from "@ant-design/icons"
import { Flex, Tooltip, Typography } from "antd"
import { type ColumnsType } from "antd/es/table"
import React from "react"

import Icon from "../../../../../../../Icon"
import i18n from "../../../../../../../i18n.ts"
import { RowActionsMenu } from "../../../../../../../components/EntityTable/RowActionsMenu.tsx"
import { budgetFormat } from "../../../../../../../utils/number/budget-format.ts"
import type { ProjectCommission } from "../../../../../redux/project-commissions/project-commissions.types.ts"
import { budgetLinesFormat } from "../../../../helpers/budget-lines-format.tsx"

const { Text } = Typography

// Shared with ProjectViewCommissionsNewRow, which matches its editors to these
// columns by key rather than by position.
export const COMMISSION_COLUMN_KEYS = {
  userName: "user_name",
  officeMainLeadCost: "office_main_lead_cost",
  officeLeadCostPlan: "office_lead_cost_plan",
  officeReferralLeadCost: "office_referral_lead_cost",
  referralLeadCostPlan: "referral_lead_cost_plan",
  officeOtherLeadCost: "office_other_lead_cost",
  othersLeadCostPlan: "others_lead_cost_plan",
  totalLeadCost: "total_lead_cost",
  salesCommissionRatio: "sales_commission_ratio",
  salesCommissionSettled: "sales_commission_settled"
} as const

type Translate = (key: string, options?: Record<string, unknown>) => string

interface GetColumnsColumnsArgs {
  isLocked: boolean
  editAllowed?: boolean
  onSelectRow: (record: ProjectCommission, action: string) => void
  t?: Translate
}

export const SettledVsCommittedHeader = ({ t }: { t: Translate }) => (
  <Flex className="items-center justify-center gap-1">
    <span>{t("projectCommissions.settledVsCommitted")}</span>
    <Tooltip title={t("projectCommissions.settledVsCommittedHint")}>
      <ExclamationCircleOutlined className="opacity-60" />
    </Tooltip>
  </Flex>
)

export const settledVsCommitted = (
  t: Translate,
  settled: number | null | undefined,
  committed: number | null | undefined,
  planned: number | null | undefined
) =>
  budgetLinesFormat(settled ?? null, committed ?? null, {
    tooltipTitle: (
      <Flex vertical gap={2}>
        <Text className="text-white text-xs">
          {t("projectCommissions.settled", {
            amount: budgetFormat(Number(settled) || 0)
          })}
        </Text>
        <Text className="text-white text-xs">
          {t("projectCommissions.committed", {
            amount: budgetFormat(Number(committed) || 0)
          })}
        </Text>
        <Text className="text-white text-xs">
          {t("projectCommissions.planned", {
            amount: budgetFormat(Number(planned) || 0)
          })}
        </Text>
      </Flex>
    )
  })

export const getColumns = ({
  onSelectRow,
  editAllowed,
  isLocked,
  t = i18n.t.bind(i18n) as Translate
}: GetColumnsColumnsArgs): ColumnsType<ProjectCommission> => [
  {
    title: "User",
    dataIndex: "user_name",
    key: "user_name",
    width: 100,
    ellipsis: true,
    render: (name: string | null) => (
      <Text ellipsis={{ tooltip: name }}>{name}</Text>
    )
  },

  {
    title: "Lead Cost",
    dataIndex: "office_main_lead_cost",
    key: "office_main_lead_cost",
    width: 110,
    align: "center",
    render: (value: string) => <Text>{value}%</Text>
  },

  {
    title: "Office Lead Cost",
    dataIndex: "office_lead_cost_plan",
    key: "office_lead_cost_plan",
    width: 130,
    align: "center",
    render: (office_lead_cost_plan: string) => (
      <Text>{budgetFormat(office_lead_cost_plan)}</Text>
    )
  },

  {
    title: "Referral Cost",
    dataIndex: "office_referral_lead_cost",
    key: "office_referral_lead_cost",
    width: 110,
    align: "center",
    render: (value: string) => <Text>{value}%</Text>
  },

  {
    title: "Referral Lead Cost",
    dataIndex: "referral_lead_cost_plan",
    key: "referral_lead_cost_plan",
    width: 140,
    align: "center",
    render: (office_lead_cost_plan) => (
      <Text>{budgetFormat(office_lead_cost_plan)}</Text>
    )
  },
  {
    title: "Others Cost",
    dataIndex: "office_other_lead_cost",
    key: "office_other_lead_cost",
    width: 110,
    align: "center",
    render: (value: string | null) => <Text>{value}%</Text>
  },

  {
    title: "Others Lead Cost",
    dataIndex: "others_lead_cost_plan",
    key: "others_lead_cost_plan",
    width: 130,
    align: "center",
    render: (others_lead_cost_plan) => (
      <Text>{budgetFormat(others_lead_cost_plan)}</Text>
    )
  },
  {
    title: "Total Lead Cost",
    dataIndex: "total_lead_cost",
    key: "total_lead_cost",
    width: 130,
    align: "center",
    render: (total_lead_cost) => <Text>{budgetFormat(total_lead_cost)}</Text>
  },
  {
    title: "Commission Ratio",
    dataIndex: "sales_commission_ratio",
    key: "sales_commission_ratio",
    width: 140,
    align: "center",
    render: (value: string | null) => <Text>{value}%</Text>
  },

  {
    title: <SettledVsCommittedHeader t={t} />,
    dataIndex: "sales_commission_settled",
    key: "sales_commission_settled",
    width: 200,
    align: "center",
    render: (_, record) =>
      settledVsCommitted(
        t,
        record?.sales_commission_settled,
        record?.sales_commission_committed,
        record?.sales_commission_plan
      )
  },

  {
    title: "Actions",
    width: 90,
    hidden: !editAllowed,
    align: "center",
    fixed: "right",
    render: (_, record) => (
      <Flex gap={"small"} justify={"center"} className={"w-20 mx-auto"}>
        <RowActionsMenu
          disabled={isLocked}
          items={[
            {
              key: "edit_row",
              label: "Edit",
              icon: <Icon type={"Edit"} />,
              onClick: () => onSelectRow(record, "edit_row")
            }
            // {
            //   key: "delete_row",
            //   label: "Delete",
            //   icon: <DeleteOutlined />,
            //   danger: true,
            //   onClick: () => onSelectRow(record, "delete_row")
            // }
          ]}
        />
      </Flex>
    )
  }
]
